import { GameAudio } from '../audio/GameAudio';
import { CALM_PATIENCE_PER_CUSTOMER, type InterludeResult } from '../fight/Interlude';
import type { OpponentDef } from '../fight/types';
import { newlyUnlocked, newlyUnlockedFight, type Achievement } from '../game/achievements';
import { dailySeed, dailyShiftNumber, randomSeed } from '../game/daily';
import { Game, type CalmCarryover } from '../game/Game';
import { hashBrownDoneness, waffleStatus } from '../game/kitchen';
import { TOPPING_INFO, orderCall } from '../game/lingo';
import { shareText } from '../game/share';
import {
  TOPPINGS,
  type ActionResult,
  type DifficultyId,
  type GameEvent,
  type GameResult,
  type Mode,
  type Topping,
} from '../game/types';
import { JUKEBOX, SCENE_H, SCENE_W } from '../render/layout';
import { SceneRenderer } from '../render/scene';
import { h, toggleClass } from '../ui/dom';
import { FightScreen } from '../ui/fightScreen';
import { Hud } from '../ui/hud';
import { IRON_KEYS, Kitchen, SLOT_KEYS } from '../ui/kitchen';
import { SceneOverlay } from '../ui/sceneOverlay';
import {
  Modal,
  achievementsContent,
  howToContent,
  introScreen,
  settingsContent,
} from '../ui/screens/modals';
import { ResultsScreen } from '../ui/screens/results';
import { TitleScreen } from '../ui/screens/title';
import { seatLabel, TicketRail } from '../ui/tickets';
import { Toaster } from '../ui/toast';
import {
  loadProgress,
  loadSettings,
  recordResult,
  saveProgress,
  saveSettings,
  type Progress,
  type Settings,
} from './persistence';

type Screen = 'title' | 'intro' | 'playing' | 'paused' | 'ending' | 'results' | 'fight';

/** What a calmed troublemaker says when they come back as a customer. */
function comebackLine(o: OpponentDef): string {
  const order = o.lines.calm.replace(/^\.+\s*/, '');
  return `Remember me? ${order.charAt(0).toUpperCase()}${order.slice(1)}`;
}

const TOPPING_BY_KEY = new Map<string, Topping>(
  TOPPINGS.map((t) => [TOPPING_INFO[t].key.toLowerCase(), t]),
);

export class App {
  private readonly root: HTMLElement;
  private settings: Settings = loadSettings();
  private progress: Progress = loadProgress();
  private readonly audio = new GameAudio();
  private game: Game | null = null;
  private screen: Screen = 'title';
  private last = performance.now();
  private unsubGame: (() => void) | null = null;
  private lastResult: GameResult | null = null;
  private lastShare = '';
  private hintsOn = false;
  private pending: { mode: Mode; difficulty: DifficultyId } | null = null;
  /** Dev-only: stop the clock without opening the pause menu. */
  private frozen = false;
  /** Shifts worked back-to-back this session; the shift-change fights get rowdier. */
  private sessionNights = 0;
  /** The shift-change result waiting to be applied to the next shift. */
  private pendingCalm: InterludeResult | null = null;
  private fightPractice = false;

  // UI
  private readonly gameEl: HTMLElement;
  private readonly sceneCanvas: HTMLCanvasElement;
  private readonly renderer: SceneRenderer;
  private readonly overlay: SceneOverlay;
  private readonly hud = new Hud();
  private readonly rail = new TicketRail();
  private readonly kitchen: Kitchen;
  private readonly toaster = new Toaster();
  private readonly title: TitleScreen;
  private readonly results: ResultsScreen;
  private readonly modal = new Modal();
  private readonly catchLayer: HTMLElement;
  private readonly announcer: HTMLElement;
  private readonly pauseMenu: HTMLElement;
  private readonly fight: FightScreen;
  private intro: HTMLElement | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.sceneCanvas = h('canvas', { class: 'scene__canvas', 'aria-label': 'The dining room' });
    this.renderer = new SceneRenderer(this.sceneCanvas);
    this.overlay = new SceneOverlay(this.renderer, () => this.settings.keyHints);
    this.kitchen = new Kitchen({
      tapSlot: (i) => this.act(this.game?.tapGrillSlot(i)),
      tossSlot: (i) => this.act(this.game?.tossGrillSlot(i)),
      tapIron: (i) => this.act(this.game?.tapIron(i)),
      tossIron: (i) => this.act(this.game?.tossIron(i)),
      topping: (t) => this.act(this.game?.toggleTopping(t)),
      drop: () => this.act(this.game?.dropHashBrowns()),
      waffle: (k) => this.act(this.game?.pourWaffle(k)),
      eggs: (s) => this.act(this.game?.cookEggs(s)),
      bacon: () => this.act(this.game?.pullBacon()),
    });
    this.rail.onSelect = (id) => this.act(this.game?.selectTicket(id));

    const scene = h('section', { class: 'scene' }, this.sceneCanvas, this.overlay.el);
    this.sceneCanvas.addEventListener('pointerdown', (e) => this.onScenePointer(e));
    this.hud.pauseButton.addEventListener('click', () => this.pause());
    this.hud.muteButton.addEventListener('click', () => this.toggleMute());

    this.gameEl = h(
      'main',
      { class: 'game', 'aria-label': 'The line' },
      this.hud.el,
      scene,
      this.rail.el,
      this.rail.callbar,
      this.kitchen.el,
    );

    this.catchLayer = h('div', { class: 'catch-layer', 'aria-hidden': 'true' });
    this.catchLayer.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.game?.catchChair();
    });

    this.pauseMenu = this.buildPauseMenu();

    this.title = new TitleScreen({
      daily: () => this.begin('daily', 'standard'),
      free: (d) => {
        this.settings = { ...this.settings, difficulty: d };
        saveSettings(this.settings);
        this.begin('free', d);
      },
      fight: () => this.startFight(true),
      howTo: () => this.modal.open('HOW TO WORK THE LINE', howToContent()),
      achievements: () =>
        this.modal.open('ACHIEVEMENTS', achievementsContent(new Set(this.progress.achievements))),
      settings: () => this.openSettings(),
    });
    this.results = new ResultsScreen({
      share: () => void this.share(),
      again: () => this.startFight(false),
      menu: () => this.toTitle(),
    });
    this.announcer = h('div', { class: 'sr-only', 'aria-live': 'polite' });
    this.fight = new FightScreen(this.audio, {
      onComplete: (result, next) => this.afterFight(result, next),
    });

    root.append(
      this.gameEl,
      this.title.el,
      this.results.el,
      this.fight.el,
      this.pauseMenu,
      this.catchLayer,
      this.modal.el,
      this.toaster.el,
      this.announcer,
    );
    this.applySettings();
    this.refreshTitle();
    this.setScreen('title');

    window.addEventListener('keydown', (e) => this.onKey(e));
    window.addEventListener('keyup', (e) => {
      if (this.screen === 'fight' && this.fight.handleKey(e, false)) e.preventDefault();
    });
    window.addEventListener('blur', () => this.fight.releaseAll());
    window.addEventListener('resize', () => this.title.resize());
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      if (this.screen === 'playing') this.pause();
      if (this.screen === 'fight') {
        this.fight.releaseAll();
        this.fight.pause();
      }
    });
    // Browsers only allow audio after a gesture; the first tap anywhere unlocks it.
    const unlock = (): void => {
      this.audio.unlock();
      this.applySettings();
      if (this.screen === 'title') this.audio.titleMusic();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });

    requestAnimationFrame(this.frame);

    if (import.meta.env.DEV) {
      // Dev-only handle for poking at the simulation from the console.
      (window as unknown as { __app: unknown }).__app = {
        app: this,
        game: () => this.game,
        pause: () => this.pause(),
        resume: () => this.resume(),
        freeze: (on: boolean) => {
          this.frozen = on;
        },
        /** Advance the simulation in 1/60 s steps, then draw one frame. */
        step: (seconds: number) => {
          for (let t = 0; t < seconds && this.game; t += 1 / 60) this.game.update(1 / 60);
          this.tick(1 / 60);
        },
        render: () => this.tick(1 / 60),
      };
    }
  }

  /* ───────────────────────── screens ───────────────────────── */

  private setScreen(screen: Screen): void {
    this.screen = screen;
    this.root.dataset.screen = screen;
    toggleClass(this.title.el, 'is-on', screen === 'title');
    toggleClass(this.results.el, 'is-on', screen === 'results');
    toggleClass(this.pauseMenu, 'is-on', screen === 'paused');
    toggleClass(this.fight.el, 'is-on', screen === 'fight');
    toggleClass(
      this.gameEl,
      'is-on',
      screen === 'playing' || screen === 'paused' || screen === 'ending' || screen === 'intro',
    );
    if (screen === 'title') this.title.resize();
  }

  private refreshTitle(): void {
    const n = dailyShiftNumber();
    const best = this.progress.daily[String(n)];
    const note =
      this.progress.shiftsWorked === 0
        ? 'Your first shift. Dottie will show you the ropes.'
        : `Shifts worked: ${this.progress.shiftsWorked} · Survived: ${this.progress.shiftsSurvived}`;
    this.title.setDaily(n, best, note);
  }

  private toTitle(): void {
    this.endGame();
    // Leaving to the menu ends the run of back-to-back shifts.
    this.sessionNights = 0;
    this.pendingCalm = null;
    this.setScreen('title');
    this.refreshTitle();
    this.audio.resume();
    this.audio.titleMusic();
  }

  private begin(mode: Mode, difficulty: DifficultyId): void {
    this.audio.unlock();
    this.applySettings();
    this.audio.resume();
    // Dottie's pep talk gets the doo-wop; the shift itself starts the jukebox.
    this.audio.titleMusic();
    this.pending = { mode, difficulty };
    this.modal.close();
    this.intro?.remove();
    const intro = introScreen(!this.progress.seenIntro, () => this.startShift());
    this.intro = intro.el;
    this.root.append(intro.el);
    this.setScreen('intro');
    intro.start();
  }

  private startShift(): void {
    this.intro?.remove();
    this.intro = null;
    if (!this.progress.seenIntro) {
      this.progress = { ...this.progress, seenIntro: true };
      saveProgress(this.progress);
    }
    const { mode, difficulty } = this.pending ?? {
      mode: 'daily' as Mode,
      difficulty: 'standard' as DifficultyId,
    };
    const shiftNumber = dailyShiftNumber();
    const firstShift = this.progress.shiftsWorked === 0;
    this.endGame();
    const calm = this.calmCarryover(this.pendingCalm);
    this.pendingCalm = null;
    this.sessionNights++;
    const game = new Game({
      seed: mode === 'daily' ? dailySeed(shiftNumber) : randomSeed(),
      mode,
      difficulty: mode === 'daily' ? 'standard' : difficulty,
      shiftNumber,
      firstShift,
      ...(calm ? { calm } : {}),
    });
    this.game = game;
    this.hintsOn = firstShift || game.options.difficulty === 'trainee';
    this.rail.reset();
    this.overlay.reset();
    this.renderer.reset();
    this.toaster.clear();
    this.unsubGame = game.events.onAny((e) => this.onGameEvent(e, game));
    this.audio.attach(game);
    this.setScreen('playing');
    this.last = performance.now();
  }

  /* ───────────────────────── the shift change ───────────────────────── */

  private calmCarryover(result: InterludeResult | null): CalmCarryover | undefined {
    if (!result) return undefined;
    return {
      calmed: result.calmed,
      patienceBonus: result.calmed * CALM_PATIENCE_PER_CUSTOMER,
      returning: result.bouts
        .filter((b) => b.outcome === 'calmed')
        .map((b) => ({
          name: b.opponent.name,
          look: b.opponent.side.look,
          line: comebackLine(b.opponent),
        })),
    };
  }

  /** Between shifts (or from the menu): the brawl to calm the crowd down. */
  private startFight(practice: boolean): void {
    this.audio.unlock();
    this.applySettings();
    this.audio.resume();
    this.modal.close();
    this.endGame();
    this.fightPractice = practice;
    const level = practice ? 1 : Math.max(1, this.sessionNights);
    this.fight.start({
      level,
      seed: randomSeed(),
      practice,
      label: practice ? 'PRACTICE' : `NIGHT ${level} → ${level + 1}`,
      reducedMotion: this.settings.reducedMotion,
      showKeys: this.settings.keyHints,
      unlock: (r) => this.unlockFight(r),
    });
    this.setScreen('fight');
  }

  private unlockFight(result: InterludeResult): Achievement[] {
    const unlocked = newlyUnlockedFight(result, new Set(this.progress.achievements));
    if (unlocked.length) {
      this.progress = {
        ...this.progress,
        achievements: [...this.progress.achievements, ...unlocked.map((a) => a.id)],
      };
      saveProgress(this.progress);
    }
    return unlocked;
  }

  private afterFight(result: InterludeResult | null, next: 'clockIn' | 'menu'): void {
    if (next === 'menu') {
      this.toTitle();
      return;
    }
    this.pendingCalm = result;
    // The next night after a daily shift is a fresh free-play night, so the
    // daily itself stays comparable between players.
    const last = this.lastResult;
    const fromDaily = this.fightPractice || !last || last.mode === 'daily';
    const mode: Mode = fromDaily ? 'free' : last.mode;
    const difficulty: DifficultyId = this.fightPractice
      ? this.settings.difficulty
      : fromDaily
        ? 'standard'
        : last.difficulty;
    this.begin(mode, difficulty);
  }

  private endGame(): void {
    this.unsubGame?.();
    this.unsubGame = null;
    this.audio.detach();
    this.game = null;
    toggleClass(this.catchLayer, 'is-on', false);
  }

  pause(): void {
    if (this.screen !== 'playing') return;
    this.setScreen('paused');
    this.audio.pause();
  }

  resume(): void {
    if (this.screen !== 'paused') return;
    this.modal.close();
    this.setScreen('playing');
    this.audio.resume();
    this.last = performance.now();
  }

  private buildPauseMenu(): HTMLElement {
    const b = (label: string, fn: () => void, cls = 'btn-secondary'): HTMLButtonElement => {
      const el = h('button', { class: cls, type: 'button' }, label);
      el.addEventListener('click', fn);
      return el;
    };
    return h(
      'div',
      { class: 'pause', role: 'dialog', 'aria-label': 'Paused' },
      h(
        'div',
        { class: 'pause__card' },
        h('h2', null, 'ON BREAK'),
        h('p', null, 'The grill waits for no one. Except right now. Right now it waits.'),
        b('BACK TO THE LINE', () => this.resume(), 'cta'),
        b('RESTART SHIFT', () => {
          this.audio.resume();
          this.begin(
            this.game?.options.mode ?? 'daily',
            this.game?.options.difficulty ?? 'standard',
          );
        }),
        b('HOW TO PLAY', () => this.modal.open('HOW TO WORK THE LINE', howToContent())),
        b('SETTINGS', () => this.openSettings()),
        b('QUIT TO TITLE', () => this.toTitle(), 'btn-ghost'),
      ),
    );
  }

  private openSettings(): void {
    const render = (): HTMLElement =>
      settingsContent(
        this.settings,
        (s) => {
          this.settings = s;
          saveSettings(s);
          this.applySettings();
          this.modal.open('SETTINGS', render());
        },
        this.audio.voice.supported,
      );
    this.modal.open('SETTINGS', render());
  }

  private applySettings(): void {
    const m = this.settings.muted;
    this.audio.apply({
      sfx: this.settings.sfx && !m,
      music: this.settings.music && !m,
      voice: this.settings.voice && !m,
    });
    this.renderer.reducedMotion = this.settings.reducedMotion;
    this.title.setReducedMotion(this.settings.reducedMotion);
    toggleClass(this.root, 'reduced-motion', this.settings.reducedMotion);
    toggleClass(this.root, 'show-keys', this.settings.keyHints);
    this.hud.setMuted(m);
  }

  private toggleMute(): void {
    this.settings = { ...this.settings, muted: !this.settings.muted };
    saveSettings(this.settings);
    this.applySettings();
    this.toaster.show(this.settings.muted ? 'Sound off' : 'Sound on', 'info');
  }

  /* ───────────────────────── game events ───────────────────────── */

  private onGameEvent(e: GameEvent, game: Game): void {
    this.renderer.handle(e, game);
    this.overlay.handle(e);
    switch (e.type) {
      case 'ticketCreated':
        this.announce(
          `New ticket ${e.ticket.no}, ${seatLabel(e.ticket.seatId)}: ${e.ticket.tags.includes('usual') ? 'the usual' : orderCall(e.ticket.order)}`,
        );
        break;
      case 'banner':
        this.announce(`${e.text}. ${e.sub ?? ''}`);
        break;
      case 'chairThrown':
        this.announce('A chair is flying at you! Press space or tap to catch it.');
        break;
      case 'chairResolved':
        this.buzz(e.outcome === 'caught' || e.outcome === 'perfect' ? 30 : [80, 40, 110]);
        break;
      case 'customerLeft':
        if (e.angry) this.buzz(20);
        break;
      case 'over':
        this.onOver(e.result);
        break;
      default:
        break;
    }
  }

  /** Light haptics on phones that support it (skipped with reduced motion). */
  private buzz(pattern: number | number[]): void {
    if (this.settings.reducedMotion || typeof navigator.vibrate !== 'function') return;
    try {
      navigator.vibrate(pattern);
    } catch {
      /* not allowed without user activation — harmless */
    }
  }

  private announce(text: string): void {
    this.announcer.textContent = text;
  }

  private onOver(result: GameResult): void {
    this.lastResult = result;
    this.setScreen('ending');
    const url = `${location.origin}${location.pathname}`;
    this.lastShare = shareText(result, url);
    const unlocked: Achievement[] = newlyUnlocked(result, new Set(this.progress.achievements));
    this.progress = recordResult(
      this.progress,
      result,
      this.lastShare,
      unlocked.map((a) => a.id),
    );
    saveProgress(this.progress);
    setTimeout(
      () => {
        if (this.screen !== 'ending') return;
        this.setScreen('results');
        this.results.show(result, unlocked, this.settings.reducedMotion);
      },
      result.reason === 'closed' ? 900 : 1600,
    );
  }

  private async share(): Promise<void> {
    const text = this.lastShare;
    if (!text) return;
    const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false;
    if (coarse && typeof navigator.share === 'function') {
      try {
        await navigator.share({ text });
        return;
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      this.toaster.show('Copied! Paste it in the group chat.', 'good');
    } catch {
      const area = h('textarea', { class: 'sr-only' });
      area.value = text;
      document.body.append(area);
      area.select();
      const ok = document.execCommand?.('copy') ?? false;
      area.remove();
      this.toaster.show(
        ok ? 'Copied! Paste it in the group chat.' : 'Couldn’t copy — long-press to copy instead.',
        ok ? 'good' : 'error',
      );
    }
  }

  /* ───────────────────────── input ───────────────────────── */

  private act(result: ActionResult | undefined): void {
    if (!result || this.screen !== 'playing') return;
    if (!result.ok && result.reason) {
      this.toaster.show(result.reason);
      this.game?.events.emit({ type: 'sfx', name: 'error' });
    }
  }

  private onScenePointer(e: PointerEvent): void {
    const game = this.game;
    if (!game || this.screen !== 'playing') return;
    const rect = this.sceneCanvas.getBoundingClientRect();
    const lx = ((e.clientX - rect.left) / rect.width) * SCENE_W;
    const ly = ((e.clientY - rect.top) / rect.height) * SCENE_H;
    if (
      lx >= JUKEBOX.x &&
      lx <= JUKEBOX.x + JUKEBOX.w &&
      ly >= JUKEBOX.y &&
      ly <= JUKEBOX.y + JUKEBOX.h
    ) {
      const song = this.audio.nextSong();
      if (song) this.toaster.show(`♪ Now playing: “${song.title}”`, 'info');
      return;
    }
    const c = this.renderer.customerAt(game, lx, ly);
    if (!c) return;
    if (c.drinksCoffee && c.state === 'waiting' && c.mug < 0.85) {
      this.act(game.refillCoffee(c.id));
    } else if (c.ticketId !== null) {
      this.act(game.selectTicket(c.ticketId));
    }
  }

  private onKey(e: KeyboardEvent): void {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
    const key = e.key.toLowerCase();

    if (this.modal.isOpen) {
      if (key === 'escape') this.modal.close();
      return;
    }
    if (this.screen === 'fight') {
      if (this.fight.handleKey(e, true)) e.preventDefault();
      return;
    }
    if (this.screen === 'paused') {
      if (key === 'escape' || key === 'p') {
        e.preventDefault();
        this.resume();
      }
      return;
    }
    if (this.screen !== 'playing' || !this.game) return;
    const game = this.game;

    if (game.chair && !game.chair.outcome) {
      if (key === ' ' || key === 'enter') {
        e.preventDefault();
        game.catchChair();
      }
      return;
    }
    if (e.repeat) return;

    const topping = TOPPING_BY_KEY.get(key);
    const slot = SLOT_KEYS.findIndex((k) => k.toLowerCase() === key);
    const iron = IRON_KEYS.findIndex((k) => k.toLowerCase() === key);
    let handled = true;
    if (key === 'escape' || key === 'p') this.pause();
    else if (key === 'm') this.toggleMute();
    else if (/^[1-5]$/.test(key)) this.act(game.selectTicketAt(Number(key) - 1));
    else if (topping) this.act(game.toggleTopping(topping));
    else if (key === 'q') this.act(game.dropHashBrowns());
    else if (key === 'w') this.act(game.pourWaffle('classic'));
    else if (key === 'e') this.act(game.pourWaffle('pecan'));
    else if (key === 'r') this.act(game.cookEggs('scrambled'));
    else if (key === 't') this.act(game.cookEggs('overEasy'));
    else if (key === 'y') this.act(game.pullBacon());
    else if (slot >= 0) this.act(e.shiftKey ? game.tossGrillSlot(slot) : game.tapGrillSlot(slot));
    else if (iron >= 0) this.act(e.shiftKey ? game.tossIron(iron) : game.tapIron(iron));
    else if (key === ' ' || key === 'enter') {
      if (e.shiftKey) {
        const active = game.activeTicket();
        const s = active ? game.slotFor(active.id) : undefined;
        this.act(s ? game.tossGrillSlot(s.id) : { ok: false, reason: 'Nothing to toss.' });
      } else {
        this.act(game.plateActive());
      }
    } else if (key === 'o') this.act(game.refillNeediest());
    else handled = false;
    if (handled) e.preventDefault();
  }

  /* ───────────────────────── tutorial hints ───────────────────────── */

  private tutorialHint(game: Game): string {
    if (!this.hintsOn || game.stats.served >= 8) return '';
    const t = game.activeTicket();
    const thirsty = [...game.customers.values()].some(
      (c) => c.state === 'waiting' && c.drinksCoffee && c.mug < 0.3,
    );
    if (!t)
      return thirsty
        ? '☕ Tap a customer with an empty mug to refill their coffee.'
        : 'Customers are coming in. Their tickets hang here.';
    if (!game.isRevealed(t)) return 'Regulars don’t read the menu. Give it a second…';
    const o = t.order;
    if (o.hashBrowns && !t.plated.hashBrowns) {
      const slot = game.slotFor(t.id);
      if (!slot) return 'Tap DROP (or any topping) to scatter hash browns on the grill.';
      const missing = o.hashBrowns.toppings.filter((x) => !slot.toppings.includes(x));
      if (missing.length)
        return `Add: ${missing.map((x) => `${TOPPING_INFO[x].lingo} = ${TOPPING_INFO[x].ingredient}`).join(', ')}`;
      const d = hashBrownDoneness(slot.time, game.cook);
      const want = o.hashBrowns.well ? 'crispy' : 'golden';
      if (d === 'raw' || (want === 'crispy' && d === 'golden'))
        return `Let them cook… tap the grill when ${want.toUpperCase()}.`;
      if (d === want) return 'NOW — tap the grill to plate them!';
    }
    if (o.waffle && !t.plated.waffle) {
      const iron = game.ironFor(t.id);
      if (!iron) return `Pour ${o.waffle === 'pecan' ? 'PECAN' : 'WAFFLE'} batter into an iron.`;
      if (waffleStatus(iron.time, game.cook) === 'ready')
        return 'DING! Tap the iron to plate the waffle.';
    }
    if (o.eggs && !t.plated.eggs && !game.lineJobFor(t.id, 'eggs'))
      return `Mark the eggs: ${o.eggs === 'scrambled' ? 'SCRAMBLED' : 'OVER EASY'}.`;
    if (o.bacon && !t.plated.bacon && !game.lineJobFor(t.id, 'bacon')) return 'Pull the BACON.';
    if (thirsty) return '☕ Tap a customer with an empty mug to refill their coffee.';
    return game.openTickets().length > 1 ? 'Juggle! Tap another ticket while this one cooks.' : '';
  }

  /* ───────────────────────── loop ───────────────────────── */

  private readonly frame = (now: number): void => {
    const dt = Math.min(0.05, Math.max(0, (now - this.last) / 1000));
    this.last = now;
    this.tick(dt);
    requestAnimationFrame(this.frame);
  };

  private tick(dt: number): void {
    const game = this.game;
    try {
      if (game && this.screen === 'playing' && !this.frozen) game.update(dt);
      if (
        game &&
        (this.screen === 'playing' || this.screen === 'paused' || this.screen === 'ending')
      ) {
        this.renderer.render(
          game,
          this.screen === 'playing' || this.screen === 'ending' ? dt : 0,
          game.activeTicketId,
        );
        this.hud.update(game);
        this.rail.hint = this.tutorialHint(game);
        this.rail.update(game);
        this.kitchen.update(game, dt, this.hintsOn);
        this.overlay.update(game, dt);
        toggleClass(
          this.catchLayer,
          'is-on',
          this.screen === 'playing' && !!game.chair && !game.chair.outcome,
        );
      }
      if (this.screen === 'title') this.title.frame(dt);
      if (this.screen === 'fight') this.fight.frame(dt);
      this.audio.frame(game, this.screen === 'playing');
    } catch (err) {
      console.error(err);
    }
  }
}
